import React, { useEffect, useMemo } from "react";
import { getWorkTimeTypeColor } from "../../../../models/workShitTimeSlot/workTimeType";
import Box from "@mui/material/Box";
import { MuiSwitch } from "../../../framework/muiSwitch";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../framework/customStore";
import {
    setWorkTimeTypeIds,
    toggleWorkTimeTypeId,
    setWorking,
    setNoWork,
} from "../../../../store/monitorViewFiltersSlice";
import { Base } from "../../../../framework/base";

interface MonitorViewWorkshiftFiltersProps {
    countByWorkTimeType: { [id: string]: number };
}

export const MonitorViewWorktimeFilters = ({
    countByWorkTimeType,
}: MonitorViewWorkshiftFiltersProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const workTimeTypes = useAppSelector((state) => state.monitorViewFilters.workTimeTypes);
    const workTimeTypesLoading = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypesLoading
    );
    const selectedWorkTimeTypeIds = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypeIds
    );
    const working = useAppSelector((state) => state.monitorViewFilters.working);
    const noWork = useAppSelector((state) => state.monitorViewFilters.noWork);

    const typeFilters = useMemo(
        () =>
            workTimeTypes
                ? [...workTimeTypes].sort((a, b) =>
                    Base.stringCompare(a.name, b.name)
                )
                : null,
        [workTimeTypes]
    );

    useEffect(() => {
        if (!selectedWorkTimeTypeIds && !!typeFilters) {
            dispatch(
                setWorkTimeTypeIds(
                    typeFilters.map((f) => f.workTimeTypeId)
                )
            );
        }
    }, [selectedWorkTimeTypeIds, typeFilters, dispatch]);

    const handleWorkingToggle = (checked: boolean) => {
        dispatch(setWorking(checked));
        if (checked) {
            const workTimeTypeIds = typeFilters
                .filter((f) => f.workTimeTypeId !== null)
                .map((f) => f.workTimeTypeId);
            dispatch(setWorkTimeTypeIds(workTimeTypeIds));
        } else {
            dispatch(setWorkTimeTypeIds([]));
        }
    };

    const handleNoWorkToggle = (checked: boolean) => {
        dispatch(setNoWork(checked));
    };

    return (
        <Box sx={{padding: "6px" }}>
            {workTimeTypesLoading || !workTimeTypes || !selectedWorkTimeTypeIds ? (
                <CircularProgress />
            ) : (
                <>
                    {typeFilters.map((w) => (
                        <MuiSwitch
                            key={w.workTimeTypeId}
                            checked={selectedWorkTimeTypeIds.includes(
                                w.workTimeTypeId
                            )}
                            label={`${w.name} (${
                                countByWorkTimeType[w.workTimeTypeId] ?? 0
                            })`}
                            color={getWorkTimeTypeColor(w.type)}
                            onChange={(_, checked) =>
                                dispatch(
                                    toggleWorkTimeTypeId({
                                        id: w.workTimeTypeId,
                                        checked,
                                    })
                                )
                            }
                            size="small"
                            labelSlotProps={{ typography: { fontSize: "0.7rem" } }}
                        />
                    ))}
                    <MuiSwitch
                        checked={working}
                        label={`${t("map.working")} (${countByWorkTimeType["work"] ?? 0})`}
                        color={"brown"}
                        onChange={(_, checked) => handleWorkingToggle(checked)}
                        size="small"
                        labelSlotProps={{ typography: { fontSize: "0.7rem" } }}
                    />
                    <MuiSwitch
                        checked={noWork}
                        label={`${t("map.noWork")} (${countByWorkTimeType["noWork"] ?? 0})`}
                        color={getWorkTimeTypeColor(null)}
                        onChange={(_, checked) => handleNoWorkToggle(checked)}
                        size="small"
                        labelSlotProps={{ typography: { fontSize: "0.7rem" } }}
                    />
                </>
            )}
        </Box>
    );
};